import { useState, useEffect, useCallback, useMemo } from "react";
import { getSchoolId } from "../../services/auth";
import {
  fetchOrgSubjects,
  fetchSchoolSubjects,
  enableSchoolSubject,
  disableSchoolSubject,
  type OrgSubjectRecord,
} from "../../services/subjectApi";
import type { ApiError } from "../../services/api";
import "./SubjectMaster.css";

export default function SchoolSubjectAllocation() {
  const [schoolId] = useState(() => getSchoolId());

  /* ---- data ---- */
  const [orgSubjects, setOrgSubjects] = useState<OrgSubjectRecord[]>([]);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set());
  const [enabledMap, setEnabledMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  /* ============== Load data ============== */
  const loadData = useCallback(async () => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [org, schoolSubj] = await Promise.all([
        fetchOrgSubjects({ perPage: 200 }),
        fetchSchoolSubjects(schoolId, { perPage: 200 }),
      ]);
      setOrgSubjects(org);
      const ids = new Set<string>();
      const tempSubjects: Record<string, string> = {};
      for (const ss of schoolSubj) {
        tempSubjects[ss.organization_subject_id] = ss.id;
        if (ss.organization_subject_id) {
          ids.add(ss.organization_subject_id);
        }
      }
      setEnabledIds(ids);
      setEnabledMap(tempSubjects);
      console.log(tempSubjects)
    } catch (err) {
      const apiErr = err as ApiError;
      setErrorMsg(apiErr.message || "Failed to load subjects.");
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ============== Toggle ============== */
  const handleToggle = async (orgSubject: OrgSubjectRecord) => {
    if (!schoolId || togglingId) return;
    const enabling = !enabledIds.has(orgSubject.id);
    setTogglingId(orgSubject.id);
    setErrorMsg("");
    try {
      if (enabling) {
        await enableSchoolSubject(schoolId, orgSubject.id, enabling);
        setSuccessMsg(
          `${orgSubject.subject?.name ?? "Subject"} ${enabling ? "enabled" : "disabled"} for this school.`,
        );
        setTimeout(() => setSuccessMsg(""), 5000);
      } else {
        await disableSchoolSubject({
          schoolId: schoolId,
          schoolSubjectId: enabledMap[orgSubject.id],
        });
        setSuccessMsg(
          `${orgSubject.subject?.name ?? "Subject"} ${enabling ? "enabled" : "disabled"} for this school.`,
        );
        setTimeout(() => setSuccessMsg(""), 5000);
      }
      await loadData();
    } catch (err) {
      const apiErr = err as ApiError;
      setErrorMsg(
        apiErr.message ||
          `Failed to ${enabling ? "enable" : "disable"} subject.`,
      );
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setTogglingId(null);
    }
  };

  /* ============== Filter & sort: enabled first ============== */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = q
      ? orgSubjects.filter(
          (o) =>
            o.subject?.name.toLowerCase().includes(q) ||
            o.subject?.code.toLowerCase().includes(q),
        )
      : orgSubjects;

    const enabled: OrgSubjectRecord[] = [];
    const disabled: OrgSubjectRecord[] = [];
    for (const o of list) {
      if (enabledIds.has(o.id)) enabled.push(o);
      else disabled.push(o);
    }
    return [...enabled, ...disabled];
  }, [orgSubjects, enabledIds, search]);

  /* ============== No school selected ============== */
  if (!schoolId) {
    return (
      <div className="subjects">
        <div className="subjects__header">
          <div className="subjects__header-text">
            <h1 className="subjects__title">School Subject Allocation</h1>
            <p className="subjects__subtitle">
              Select a school from the top bar to manage subject allocation.
            </p>
          </div>
        </div>
        <div className="subjects__table-wrap">
          <div className="subjects__empty">
            <div className="subjects__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <h3 className="subjects__empty-title">No school selected</h3>
            <p className="subjects__empty-text">
              Please select a school from the top bar to manage subject
              allocation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subjects">
      {successMsg && (
        <div className="subjects__toast">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {successMsg}
          <button className="subjects__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
        </div>
      )}

      {errorMsg && (
        <div className="subjects__toast subjects__toast--error">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {errorMsg}
          <button className="subjects__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
        </div>
      )}

      <div className="subjects__header">
        <div className="subjects__header-text">
          <h1 className="subjects__title">School Subject Allocation</h1>
          <p className="subjects__subtitle">
            Enable or disable organization subjects for this school. Enabled
            subjects will be available to allocate to classes.
          </p>
        </div>
        <div className="subjects__header-actions">
          <div className="subjects__search-wrap">
            <svg
              className="subjects__search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="subjects__search"
              type="text"
              placeholder="Search subjects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="subjects__table-wrap">
        {loading ? (
          <div className="subjects__loading">
            <div className="subjects__loader">
              <span className="subjects__loader-dot" />
              <span className="subjects__loader-dot" />
              <span className="subjects__loader-dot" />
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="subjects__empty">
            <div className="subjects__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <h3 className="subjects__empty-title">
              {search ? "No subjects match" : "No organization subjects"}
            </h3>
            <p className="subjects__empty-text">
              {search
                ? "Try adjusting your search."
                : "Add subjects in the Subject Master List first, then enable them here for this school."}
            </p>
          </div>
        ) : (
          <table className="subjects__table">
            <thead>
              <tr>
                <th className="subjects__th">Subject Name</th>
                <th className="subjects__th">Code</th>
                <th className="subjects__th">Type</th>
                <th className="subjects__th">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr className="subjects__table-row" key={o.id}>
                  <td className="subjects__td subjects__td--name">
                    {o.subject?.name ?? "—"}
                  </td>
                  <td className="subjects__td subjects__td--code">
                    {o.subject?.code ?? "—"}
                  </td>
                  <td className="subjects__td">
                    <span
                      className={`subjects__badge ${o.subject?.type === "core" ? "subjects__badge--core" : "subjects__badge--elective"}`}
                    >
                      {o.subject?.type
                        ? o.subject.type.charAt(0).toUpperCase() +
                          o.subject.type.slice(1)
                        : "—"}
                    </span>
                  </td>
                  <td className="subjects__td">
                    <div className="subjects__toggle-cell">
                      {togglingId === o.id ? (
                        <svg
                          className="subjects__spinner subjects__spinner--inline"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle cx="12" cy="12" r="10" strokeWidth="3" />
                        </svg>
                      ) : (
                        <label className="subjects__toggle">
                          <input
                            type="checkbox"
                            checked={enabledIds.has(o.id)}
                            onChange={() => handleToggle(o)}
                            disabled={togglingId !== null}
                          />
                          <span className="subjects__toggle-track" />
                        </label>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
